<template>
  <div class="page-container billing-batch-detail">
    <portal to="page-name">Lotes</portal>
    <div class="card card-page" v-if="hasAccess">
      <div class="card-header">
        <div class="batch-detail-actions" v-if="batch">
          <button class="btn btn-icon btn-icon-left"
                  v-if="tissEnabled"
                  :class="[actionClass('xml'), tipClass(xmlDisabledReason),
                           {loading: busyAction === 'xml'}]"
                  :data-tooltip="xmlDisabledReason"
                  :disabled="!!busyAction || !!xmlDisabledReason"
                  @click="xml">
            <fa-icon :icon="['fal', 'cloud-download']"/>
            <span>{{ batch.status === 'closed' ? 'Gerar XML' : 'Baixar XML' }}</span>
          </button>
          <button class="btn btn-icon btn-icon-left"
                  :class="[actionClass('send'), tipClass(sendDisabledReason)]"
                  :data-tooltip="sendDisabledReason"
                  :disabled="!!busyAction || !!sendDisabledReason"
                  @click="sendModal.show = true">
            <fa-icon :icon="['fal', 'paper-plane']"/><span>Marcar como enviado</span>
          </button>
          <button class="btn btn-icon btn-icon-left"
                  :class="[actionClass('import'), tipClass(importDisabledReason)]"
                  :data-tooltip="importDisabledReason"
                  :disabled="!!busyAction || !!importDisabledReason"
                  @click="openImport">
            <fa-icon :icon="['fal', 'file-import']"/><span>Importar demonstrativo</span>
          </button>
          <button class="btn btn-gray btn-icon tooltip tooltip-bottom"
                  :class="tissEnabled ? 'btn-action' : 'btn-icon-left'"
                  :data-tooltip="coverDisabledReason || 'Capa do lote'"
                  :disabled="!!busyAction || !!coverDisabledReason"
                  @click="coverModal.show = true">
            <fa-icon :icon="['fal', 'print']"/><span v-if="!tissEnabled">Capa do lote</span>
          </button>
          <dx-dropdown class="batch-detail-more" :items="moreItems" direction="right"
                       @select="onMore">
            <span class="btn btn-gray btn-action btn-icon">
              <fa-icon :icon="['fal', 'ellipsis-h']"/>
            </span>
            <template v-slot:item="{ item }">
              <a href="#" :class="{'text-error': item.danger, 'text-gray': !!item.disabled}"
                 :title="item.disabled || null" @click.prevent>{{ item.text }}</a>
            </template>
          </dx-dropdown>
        </div>
        <h1 class="card-title">
          <router-link :to="backRoute" class="card-title-back tooltip tooltip-right"
                       data-tooltip="Voltar para Lotes">
            <fa-icon :icon="['fal', 'arrow-left']"/>
          </router-link>
          <span v-if="batch">Lote {{ batch.number }}</span>
          <span v-else>Lote</span>
          <span class="cr-type" :class="`cr-type-${batch.guideType}`" v-if="batch">
            {{ guideTypeName(batch.guideType) }}
          </span>
        </h1>
      </div>

      <div class="card-body">
        <div class="loading loading-lg mt-2" v-if="loading"></div>
        <div class="empty mt-2" v-else-if="notFound">
          <div class="empty-icon"><fa-icon :icon="['fal', 'info-circle']" size="3x"/></div>
          <p class="empty-title h5">Lote não encontrado</p>
          <p class="empty-subtitle">O lote pode ter sido removido ou pertence a outra filial.</p>
          <router-link to="/financial/billing-batches" class="btn btn-primary">
            Voltar para Lotes
          </router-link>
        </div>
        <template v-else-if="batch">
          <batch-banner :batch="batch"/>
          <batch-timeline :status="batch.status" :dates="batch.dates" :events="batch.events"/>

          <batch-issues-panel :issues="issues" :ok-message="okMessage" @action="onIssueAction"/>

          <div class="batch-detail-grid">
            <div>
              <div class="cr-summary batch-detail-summary">
                <div class="cr-summary-item text-left">
                  <small>Guias</small>
                  <div class="cr-summary-value">{{ batch.quantity }}</div>
                  <span class="cr-summary-hint">{{ itemsLabel }}</span>
                </div>
                <div class="cr-summary-item">
                  <small>Faturado</small>
                  <div class="cr-summary-value">{{ batch.totals.informed | currency }}</div>
                </div>
                <template v-if="analyzed">
                  <div class="cr-summary-item">
                    <small>Glosado</small>
                    <div class="cr-summary-value" :class="{'text-error': batch.totals.glossed > 0}">
                      {{ batch.totals.glossed | currency }}
                    </div>
                    <span class="cr-summary-hint" v-if="batch.totals.informed > 0">
                      {{ glossedPercent }} · {{ glossesLabel }}
                    </span>
                  </div>
                  <div class="cr-summary-item">
                    <small>Recursado</small>
                    <div class="cr-summary-value">{{ batch.totals.appealed | currency }}</div>
                  </div>
                  <div class="cr-summary-item">
                    <small>Recuperado</small>
                    <div class="cr-summary-value">{{ batch.totals.recovered | currency }}</div>
                  </div>
                </template>
                <div class="cr-summary-item" :class="settled ? 'done' : 'highlight'"
                     v-if="!canceled">
                  <small>{{ mainLabel }}</small>
                  <div class="cr-summary-value">{{ mainValue }}</div>
                  <span class="cr-summary-hint" v-if="receivable && receivable.dueDate">
                    {{ receivableHint }}
                  </span>
                </div>
              </div>
              <div class="batch-detail-summary-note" v-if="!analyzed && !canceled">
                <fa-icon :icon="['fal', 'info-circle']"/>
                Glosa, recurso e recuperação aparecem quando o demonstrativo de análise
                da operadora for importado.
              </div>
              <batch-facts ref="facts" :batch="batch" :editable="editableFacts"
                           @saved="onFactSaved"/>
            </div>
          </div>

          <st-tabs ref="tabs" class="batch-detail-tabs" @changed="onTabChanged">
            <st-tab id="guides" :name="tabName('Guias', batch.quantity)">
              <batch-guides-tab
                :batch="batch"
                :guides="batch.guides"
                :can-add="canEditGuides"
                :remove-disabled="removeDisabledReason"
                :printing-id="printingGuideId"
                :removing-id="removingGuideId"
                @open="openGuide"
                @print="printGuide"
                @remove="removeGuide"
                @add="addGuidesModal.show = true"
              />
            </st-tab>
            <st-tab id="financial" name="Financeiro">
              <batch-financial-tab
                :receivable="financialDetail.receivable"
                :recoveries="financialDetail.recoveries"
                :transfers="financialDetail.transfers"
                :loading="loadingFinancial"
                :printing-id="printingReceiptId"
                @open-financial="openFinancial"
                @print-receipt="printReceipt"
              />
            </st-tab>
            <st-tab id="glosses" :name="tabName('Glosas', glossesTotal)">
              <batch-glosses ref="glosses" :batch-id="batch.id" @changed="reload"/>
            </st-tab>
            <st-tab id="appeals" :name="tabName('Recursos', appealsCount)">
              <batch-appeals ref="appeals" :batch-id="batch.id" @changed="reload"/>
            </st-tab>
            <st-tab id="statements" :name="tabName('Demonstrativos', statementsCount)">
              <batch-statements ref="statements" :batch-id="batch.id" :batch-status="batch.status"
                                @changed="reload"/>
            </st-tab>
            <st-tab id="file" name="Arquivo">
              <batch-file-tab
                :batch="batch"
                :busy-action="busyAction"
                :can-xml="canXml"
                :can-print="canPrint"
                @download="xml"
                @validate="validate"
                @cover="coverModal.show = true"
              />
            </st-tab>
          </st-tabs>
        </template>
      </div>
    </div>
    <forbidden v-else></forbidden>

    <send-batch-modal
      v-if="sendModal.show"
      :show="sendModal.show"
      :batch="batch"
      @close="closeSend"
      @generate-xml="xml"
    />
    <cancel-batch-modal
      v-if="cancelModal.show"
      :show="cancelModal.show"
      :batch="batch"
      @close="closeCancel"
    />
    <add-guides-modal
      v-if="addGuidesModal.show"
      :show="addGuidesModal.show"
      :batch="batch"
      @close="closeAddGuides"
      @added="onGuidesAdded"
      @open-guide="openGuide"
    />
    <cover-print-modal
      v-if="coverModal.show"
      :show="coverModal.show"
      :batch="batch"
      @close="coverModal.show = false"
    />
    <xml-errors-modal
      v-if="xmlErrorsModal.show"
      :show="xmlErrorsModal.show"
      :batch="xmlErrorsModal.batch"
      :errors="xmlErrorsModal.errors"
      :warnings="xmlErrorsModal.warnings"
      @close="closeXmlErrors"
      @retry="retryXml"
      @open-guide="openGuide"
    />
    <import-statement-modal
      v-if="statementModal.show"
      :show="statementModal.show"
      :batch-id="batch.id"
      :insurance-id="insuranceId"
      @close="closeStatementImport"
    />
    <guide-modal
      v-if="guideModal.show"
      :show="guideModal.show"
      :account-id="guideModal.accountId"
      :readonly="batch.status !== 'closed'"
      @close="closeGuide"
      @deleted="reload"
    />
  </div>
</template>

<script>
import moment from 'moment';
import {
  LIST_BILLINGS,
  UPDATE_BILLING,
  DELETE_BILLING,
  PRINT_BILLING,
  GENERATE_BILLING_XML,
} from '@/data/actions/modules/financial';
import Forbidden from '@/components/auth/Forbidden.vue';
import currency from '@/filters/currency';
import dateFilter from '@/filters/date';
import {
  MANAGE_BILLING_GLOSSES,
  IMPORT_BILLING_STATEMENTS,
  guideTypeName,
  guideNumber,
  planRoute,
  financialRoute,
  idOf,
} from '../service';
import xmlMixin from '../mixins/xml';
import BatchBanner from './BatchBanner.vue';
import BatchTimeline from './BatchTimeline.vue';
import BatchIssuesPanel from './BatchIssuesPanel.vue';
import BatchFacts from './BatchFacts.vue';
import BatchGuidesTab from './BatchGuidesTab.vue';
import BatchFinancialTab from './BatchFinancialTab.vue';
import BatchFileTab from './BatchFileTab.vue';
import {
  BatchGlosses,
  BatchAppeals,
  BatchStatements,
  ImportStatementModal,
  GuideModal,
} from './glossTabs';
import SendBatchModal from '../modals/SendBatch.vue';
import CancelBatchModal from '../modals/CancelBatch.vue';
import AddGuidesModal from '../modals/AddGuides.vue';
import CoverPrintModal from '../modals/CoverPrint.vue';
import XmlErrorsModal from '../modals/XmlErrors.vue';

const ANALYZED = ['analyzed', 'in_appeal', 'settled'];

export default {
  mixins: [xmlMixin],
  components: {
    Forbidden,
    BatchBanner,
    BatchTimeline,
    BatchIssuesPanel,
    BatchFacts,
    BatchGuidesTab,
    BatchFinancialTab,
    BatchFileTab,
    BatchGlosses,
    BatchAppeals,
    BatchStatements,
    ImportStatementModal,
    GuideModal,
    SendBatchModal,
    CancelBatchModal,
    AddGuidesModal,
    CoverPrintModal,
    XmlErrorsModal,
  },
  data() {
    return {
      loading: true,
      notFound: false,
      batch: null,
      financialDetail: { receivable: null, recoveries: [], transfers: [] },
      loadingFinancial: false,
      financialLoaded: false,
      busyAction: null,
      printingGuideId: null,
      removingGuideId: null,
      printingReceiptId: null,
      sendModal: { show: false },
      cancelModal: { show: false },
      addGuidesModal: { show: false },
      coverModal: { show: false },
      statementModal: { show: false },
      guideModal: { show: false, accountId: null },
    };
  },
  computed: {
    hasAccess() {
      return this.$can(LIST_BILLINGS);
    },
    canUpdate() {
      return this.$can(UPDATE_BILLING);
    },
    canDelete() {
      return this.$can(DELETE_BILLING);
    },
    canPrint() {
      return this.$can(PRINT_BILLING);
    },
    canXml() {
      return this.$can(GENERATE_BILLING_XML);
    },
    canManageGlosses() {
      return this.$can(MANAGE_BILLING_GLOSSES);
    },
    canImport() {
      return this.$can(IMPORT_BILLING_STATEMENTS);
    },
    backRoute() {
      if (!this.batch) return '/financial/billing-batches';
      return {
        path: '/financial/billing-batches',
        query: { planId: idOf(this.batch.insurance.plan) },
      };
    },
    insuranceId() {
      return this.batch ? idOf(this.batch.insurance) : null;
    },
    tissEnabled() {
      return !!(this.batch && this.batch.tiss && this.batch.tiss.enabled);
    },
    analyzed() {
      return !!this.batch && ANALYZED.includes(this.batch.status);
    },
    canceled() {
      return this.batch.status === 'canceled';
    },
    fileGenerated() {
      return !!(this.batch && this.batch.file && this.batch.file.generatedAt);
    },
    itemsCount() {
      return (this.batch.guides || []).reduce((sum, guide) => sum + (guide.itemsCount || 0), 0);
    },
    itemsLabel() {
      return `${this.itemsCount} ${this.itemsCount === 1 ? 'item' : 'itens'}`;
    },
    glossedPercent() {
      const percent = (this.batch.totals.glossed / this.batch.totals.informed) * 100;
      return `${percent.toFixed(1).replace('.', ',')}%`;
    },
    glossesTotal() {
      return (this.batch && this.batch.glosses && this.batch.glosses.total) || 0;
    },
    glossesLabel() {
      return `${this.glossesTotal} ${this.glossesTotal === 1 ? 'glosa' : 'glosas'}`;
    },
    settled() {
      return this.batch.status === 'settled';
    },
    mainLabel() {
      if (this.settled) return 'Recebido';
      return this.analyzed ? 'Liberado' : 'A receber';
    },
    mainValue() {
      const { totals } = this.batch;
      if (this.settled) return currency(totals.paid);
      return currency(this.analyzed ? totals.released : totals.informed);
    },
    receivableHint() {
      if (this.receivable.status === 'paid') return 'título recebido';
      return `título vence ${dateFilter(this.receivable.dueDate)}`;
    },
    glossesOpen() {
      const byStatus = (this.batch && this.batch.glosses && this.batch.glosses.byStatus) || {};
      return byStatus.open || { count: 0, value: 0 };
    },
    appealsCount() {
      return (this.batch && this.batch.appeals && this.batch.appeals.length) || 0;
    },
    statementsCount() {
      return (this.batch && this.batch.statements && this.batch.statements.length) || 0;
    },
    receivable() {
      return (this.batch && this.batch.financial && this.batch.financial.receivable) || null;
    },
    hasPaid() {
      return !!(this.batch && this.batch.hasPaidFinancial);
    },
    issues() {
      const list = [];
      const { batch } = this;
      (batch.warnings || []).forEach((warning) => {
        if (warning.code === 'tissVersionOutdated') {
          list.push({
            level: 'warn',
            message: `Versão TISS ${batch.tiss.version} do plano está desatualizada`,
            detail: 'Obrigatória 4.03.00 desde julho/2026. Não bloqueia este lote.',
            action: { label: 'Atualizar plano', type: 'plan' },
          });
        }
        if (warning.code === 'invoicePending') {
          const deadline = batch.invoice && batch.invoice.deadlineAt;
          list.push({
            level: 'warn',
            message: 'Nota fiscal obrigatória ainda não registrada',
            detail: deadline
              ? `A operadora só paga após a NF · prazo ${dateFilter(deadline)}`
              : 'A operadora só paga após a NF.',
            action: this.canUpdate ? { label: 'Registrar NF', type: 'invoice' } : null,
          });
        }
      });
      if (batch.status === 'sent' && batch.dates.sentAt) {
        const days = moment().diff(moment(batch.dates.sentAt), 'days');
        if (days > 20) {
          list.push({
            level: 'warn',
            message: `Lote enviado há ${days} dias sem retorno`,
            detail: 'Importe o demonstrativo de análise assim que a operadora responder.',
            action: this.canImport ? { label: 'Importar demonstrativo', type: 'import' } : null,
          });
        }
      }
      if (['analyzed', 'in_appeal'].includes(batch.status) && this.glossesOpen.count > 0) {
        list.push({
          level: 'warn',
          message: `${this.glossesOpen.count} ${this.glossesOpen.count === 1 ? 'glosa aguardando' : 'glosas aguardando'} decisão`,
          detail: `${currency(this.glossesOpen.value)} · aceitar ou recursar item a item`,
          action: { label: 'Ver glosas', type: 'tab', tab: 'glosses' },
        });
      }
      if (this.receivable && this.receivable.status === 'open' && this.receivable.dueDate
        && moment(this.receivable.dueDate).isBefore(moment(), 'day')
        && !['settled', 'canceled'].includes(batch.status)) {
        list.push({
          level: 'info',
          message: `Título a receber vencido em ${dateFilter(this.receivable.dueDate)}`,
          detail: 'Confira o pagamento da operadora no financeiro.',
          action: { label: 'Ver título', type: 'tab', tab: 'financial' },
        });
      }
      if (batch.hasPaidTransfer && this.analyzed && batch.totals.glossed > 0) {
        list.push({
          level: 'info',
          message: 'Há repasse já pago neste lote',
          detail: 'A glosa abatida vira ajuste no próximo repasse do profissional.',
          action: { label: 'Ver repasses', type: 'tab', tab: 'financial' },
        });
      }
      return list;
    },
    okMessage() {
      if (!this.batch) return '';
      const { status } = this.batch;
      if (status === 'closed') {
        if (this.primaryAction === 'xml') return 'Sem pendências · pronto para gerar o XML';
        return this.tissEnabled
          ? 'Sem pendências · XML gerado, marque como enviado'
          : 'Sem pendências · imprima a capa e marque como enviado';
      }
      if (status === 'sent') return 'Sem pendências · aguardando o retorno da operadora';
      if (status === 'canceled') return 'Lote cancelado · nada a fazer';
      if (status === 'settled') return 'Lote baixado · guias encerradas';
      return 'Sem pendências no momento';
    },
    editableFacts() {
      const { status } = this.batch;
      const closed = status === 'closed';
      const active = !['settled', 'canceled'].includes(status);
      return {
        expectedPaymentAt: active && this.canUpdate,
        notes: active && this.canUpdate,
        invoice: active && this.canUpdate,
        protocol: active && !closed && this.canUpdate,
      };
    },
    canEditGuides() {
      return this.batch.status === 'closed' && !this.hasPaid && this.canUpdate;
    },
    removeDisabledReason() {
      if (this.batch.status !== 'closed') return 'Só em lote fechado';
      if (this.hasPaid) return 'Há repasse ou título pago';
      if (!this.canUpdate) return 'Sem permissão';
      return null;
    },
    xmlDisabledReason() {
      if (this.batch.status === 'canceled') return 'Lote cancelado';
      if (!this.canXml) return 'Sem permissão';
      return null;
    },
    coverDisabledReason() {
      if (this.batch.status === 'canceled') return 'Lote cancelado';
      if (!this.canPrint) return 'Sem permissão';
      return null;
    },
    sendDisabledReason() {
      const { status, dates } = this.batch;
      if (status === 'canceled') return 'Lote cancelado';
      if (status === 'sent') return `Já enviado em ${dateFilter(dates.sentAt)}`;
      if (status !== 'closed') return 'Lote já enviado';
      if (!this.canUpdate) return 'Sem permissão';
      if (this.tissEnabled && !this.fileGenerated) return 'Gere o XML antes';
      return null;
    },
    importDisabledReason() {
      const { status } = this.batch;
      if (status === 'canceled') return 'Lote cancelado';
      if (status === 'closed') return 'Envie o lote antes';
      if (status === 'settled') return 'Lote baixado';
      if (!this.canImport) return 'Sem permissão';
      return null;
    },
    settleDisabledReason() {
      const { status } = this.batch;
      if (!['sent', 'analyzed', 'in_appeal'].includes(status)) return 'Só lote enviado ou analisado';
      if (!this.canUpdate) return 'Sem permissão';
      return null;
    },
    cancelDisabledReason() {
      const { status } = this.batch;
      if (status === 'canceled') return 'Lote já cancelado';
      if (status === 'settled') return 'Lote baixado';
      if (status === 'in_appeal') return 'Há recurso enviado';
      if (this.hasPaid) return 'Há repasse ou título pago';
      if (!this.canDelete) return 'Sem permissão';
      return null;
    },
    glossDisabledReason() {
      const { status } = this.batch;
      if (['closed', 'settled', 'canceled'].includes(status)) return 'Só em lote enviado ou analisado';
      if (!this.canManageGlosses) return 'Sem permissão';
      return null;
    },
    primaryAction() {
      const { status } = this.batch;
      if (status === 'closed') {
        return this.tissEnabled && !this.fileGenerated ? 'xml' : 'send';
      }
      if (['sent', 'analyzed', 'in_appeal'].includes(status)) return 'import';
      return null;
    },
    moreItems() {
      const items = [];
      if (this.tissEnabled) {
        items.push({
          key: 'validate',
          text: 'Validar XML',
          disabled: this.batch.status === 'canceled' ? 'Lote cancelado' : null,
        });
      }
      items.push({ key: 'gloss', text: 'Lançar glosa manual', disabled: this.glossDisabledReason });
      items.push({ key: 'settle', text: 'Baixar lote', disabled: this.settleDisabledReason });
      items.push('-');
      items.push({
        key: 'cancel', text: 'Cancelar lote', danger: true, disabled: this.cancelDisabledReason,
      });
      return items;
    },
  },
  mounted() {
    if (!this.hasAccess) return;
    this.load(true);
  },
  watch: {
    '$route.params.id': function onIdChange() {
      this.load(true);
    },
    '$route.query.tab': function onTabChange(tab) {
      if (tab && this.$refs.tabs) this.$refs.tabs.selectTab(tab);
    },
  },
  methods: {
    guideTypeName,
    guideNumber,
    currency,
    dateFilter,
    actionClass(action) {
      return this.primaryAction === action ? 'btn-primary' : 'btn-gray';
    },
    tipClass(reason) {
      return reason ? 'tooltip tooltip-bottom' : '';
    },
    tabName(label, count) {
      return count > 0 ? `${label} (${count})` : label;
    },
    load(first = false) {
      if (first) this.loading = true;
      this.notFound = false;
      return this.$http.get(`/billing-batches/${this.$route.params.id}`)
        .then(({ data }) => {
          this.batch = data;
          if (this.financialLoaded) this.loadFinancial();
        })
        .catch((e) => {
          if (e && e.response && e.response.status === 404) {
            this.notFound = true;
            return;
          }
          this.$toast.error(e);
        })
        .finally(() => {
          this.loading = false;
          if (first && !this.notFound) this.$nextTick(() => this.applyQuery());
        });
    },
    reload() {
      this.refreshPanels();
      return this.load(false);
    },
    refreshPanels() {
      ['glosses', 'appeals', 'statements'].forEach((ref) => {
        const panel = this.$refs[ref];
        if (panel && panel.load) panel.load();
      });
    },
    applyQuery() {
      const { tab, import: importFlag } = this.$route.query;
      if (tab && this.$refs.tabs) this.$refs.tabs.selectTab(tab);
      if (importFlag === '1' && !this.importDisabledReason) this.openImport();
    },
    onTabChanged({ tab }) {
      if (tab.hash === 'financial' && !this.financialLoaded) this.loadFinancial();
    },
    loadFinancial() {
      this.loadingFinancial = !this.financialLoaded;
      return this.$http.get(`/billing-batches/${this.batch.id}/financial`)
        .then(({ data }) => {
          this.financialDetail = {
            receivable: data.receivable || null,
            recoveries: data.recoveries || [],
            transfers: data.transfers || [],
          };
          this.financialLoaded = true;
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.loadingFinancial = false;
        });
    },
    onFactSaved({ batch }) {
      this.batch = { ...this.batch, ...batch, guides: this.batch.guides };
      if (this.financialLoaded) this.loadFinancial();
    },
    onIssueAction(issue) {
      const { action } = issue;
      if (!action) return;
      if (action.type === 'plan') this.openPlan();
      if (action.type === 'import') this.openImport();
      if (action.type === 'invoice' && this.$refs.facts) this.$refs.facts.edit('invoice');
      if (action.type === 'tab' && this.$refs.tabs) this.$refs.tabs.selectTab(action.tab);
    },
    openGuide(id) {
      this.guideModal.accountId = id;
      this.guideModal.show = true;
    },
    closeGuide(saved) {
      this.guideModal.show = false;
      this.guideModal.accountId = null;
      if (saved) this.reload();
    },
    openPlan() {
      this.$router.push(planRoute(this.insuranceId, idOf(this.batch.insurance.plan)));
    },
    openFinancial(financial) {
      this.$router.push(financialRoute(financial.id));
    },
    printGuide(id) {
      this.printingGuideId = id;
      this.$file.print(`/billing-accounts/${id}/print`)
        .catch(() => {})
        .finally(() => {
          this.printingGuideId = null;
        });
    },
    printReceipt(transfer) {
      this.printingReceiptId = transfer.bundleId;
      this.$file.print(`/billing-transfers/${transfer.bundleId}/print`)
        .catch(() => {})
        .finally(() => {
          this.printingReceiptId = null;
        });
    },
    removeGuide(guide) {
      const number = guideNumber(guide) || '';
      this.$dialog.show('', {
        html: '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + `<div>Deseja realmente retirar a guia ${number} do lote ${this.batch.number}? `
          + 'Ela volta para \'Aberta\' e o título e os repasses serão recalculados.</div></div>',
        buttons: [
          { label: 'Não', classes: '' },
          {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: (close) => {
              close();
              this.removingGuideId = guide.id;
              this.$http.delete(`/billing-batches/${this.batch.id}/guides/${guide.id}`)
                .then(({ data }) => {
                  this.$toast.success('Guia retirada do lote.');
                  this.batch = data;
                  if (this.financialLoaded) this.loadFinancial();
                })
                .catch((e) => {
                  this.$toast.error(e);
                })
                .finally(() => {
                  this.removingGuideId = null;
                });
            },
          },
        ],
      });
    },
    closeAddGuides() {
      this.addGuidesModal.show = false;
    },
    onGuidesAdded(batch) {
      this.batch = batch;
      if (this.financialLoaded) this.loadFinancial();
    },
    async xml() {
      if (this.xmlDisabledReason) return;
      this.busyAction = 'xml';
      try {
        const done = await this.downloadXml(this.batch);
        if (done && this.batch.status === 'closed') await this.reload();
      } catch (e) {
        this.$toast.error(e);
      } finally {
        this.busyAction = null;
      }
    },
    retryXml() {
      this.closeXmlErrors();
      this.xml();
    },
    async validate() {
      this.busyAction = 'validate';
      try {
        const validation = await this.validateXml(this.batch);
        if (validation.errors && validation.errors.length > 0) {
          this.openXmlErrors(this.batch, validation);
        } else {
          this.$toast.success('XML válido. Nenhum erro encontrado.');
        }
      } catch (e) {
        this.$toast.error(e);
      } finally {
        this.busyAction = null;
      }
    },
    onMore(item) {
      if (!item || typeof item !== 'object') return;
      if (item.disabled) {
        this.$toast.error(item.disabled);
        return;
      }
      if (item.key === 'validate') this.validate();
      if (item.key === 'gloss' && this.$refs.tabs) this.$refs.tabs.selectTab('glosses');
      if (item.key === 'settle') this.settle();
      if (item.key === 'cancel') this.cancelModal.show = true;
    },
    settle() {
      const today = moment().format('YYYY-MM-DD');
      this.$dialog.show('', {
        html: '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + `<div>Confirmar a baixa do lote ${this.batch.number} em ${dateFilter(today)}? `
          + 'As guias serão encerradas e as glosas em aberto marcadas como aceitas.</div></div>',
        buttons: [
          { label: 'Não', classes: '' },
          {
            label: 'Sim',
            classes: 'btn-primary ml-2',
            click: (close) => {
              close();
              this.busyAction = 'settle';
              this.$http.post(`/billing-batches/${this.batch.id}/settle`, { settledAt: today })
                .then(() => {
                  this.$toast.success('Lote baixado. Guias encerradas.');
                  return this.reload();
                })
                .catch((e) => {
                  this.$toast.error(e);
                })
                .finally(() => {
                  this.busyAction = null;
                });
            },
          },
        ],
      });
    },
    closeSend(sent) {
      this.sendModal.show = false;
      if (sent) this.reload();
    },
    closeCancel(canceled) {
      this.cancelModal.show = false;
      if (canceled) this.reload();
    },
    openImport() {
      if (this.importDisabledReason) {
        this.$toast.error(this.importDisabledReason);
        return;
      }
      this.statementModal.show = true;
    },
    closeStatementImport(applied) {
      this.statementModal.show = false;
      if (applied) this.reload();
    },
  },
};
</script>

<style lang="scss">
@import "../batch.scss";

.billing-batch-detail {
  .card-header {
    .batch-detail-actions {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      float: right;
      gap: $layout-spacing-sm;
      justify-content: flex-end;
      margin-left: $layout-spacing;
      .tooltip::after {
        white-space: nowrap;
      }
    }
    .batch-detail-more {
      .btn {
        height: 1.4rem;
        width: 1.4rem;
      }
      .menu {
        min-width: 9rem;
        .menu-item a.text-gray {
          cursor: not-allowed;
        }
      }
    }
  }
  .batch-detail-grid {
    align-items: start;
    display: grid;
    gap: $layout-spacing-lg;
    grid-template-columns: minmax(0, 1fr);
    margin-top: $layout-spacing-lg;
    .batch-detail-summary {
      margin-top: 0;
    }
    .batch-detail-summary-note {
      align-items: baseline;
      color: $gray-color-dark;
      display: flex;
      font-size: $font-size-xs;
      gap: .3rem;
      margin-top: $layout-spacing-sm;
      svg {
        color: $gray-color;
      }
    }
  }
  .batch-detail-tabs {
    margin-top: $layout-spacing-lg;
    .tab-panel .cr-panel {
      margin-top: $layout-spacing;
    }
  }
  @media screen and (max-width: $size-md) {
    .card-header .batch-detail-actions .btn span {
      display: none;
    }
    .batch-detail-grid {
      grid-template-columns: 1fr;
      .batch-issues, .batch-issues-ok {
        order: -1;
      }
    }
  }
}
</style>
